import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { admin as adminApi, users as usersApi, subscriptions as subsApi,
         donations as donationsApi, askGad as askGadApi } from "../lib/api.js";
import { useApi } from "../hooks/index.js";
import { Spinner, Empty, ProgBar, Badge, CloseX } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const PLT_LABEL  = { apple_iap: "Apple IAP", google_play: "Google Play", stripe: "Stripe", flutterwave: "Flutterwave", scholarship: "Scholarship", manual: "Manual" };
const PLT_COLOR  = { apple_iap: "var(--ink)", google_play: "#34a853", stripe: "#635bff", flutterwave: "#f5a623", scholarship: "var(--teal)", manual: "var(--ghost)" };
const STATUS_COLOR = { COMPLETED: "var(--teal)", PENDING: "var(--amber)", FAILED: "var(--rose)", REFUNDED: "var(--ghost)" };

function SparkBars({ color, hi }) {
  const vals = [55, 62, 58, 70, 75, 68, 80, 72, 85, 82, 90];
  return (
    <div className="spark">
      {vals.map((h, i) => (
        <div key={i} className="sp-bar" style={{ height: `${h}%`, background: i === vals.length - 1 ? hi : color }} />
      ))}
    </div>
  );
}

function StatCard({ cls, label, value, sub, chg, chgType = "chg-up", sparkColor, sparkHi, onClick, "data-testid": testId }) {
  const { t } = useLang();
  return (
    <div data-testid={testId} className={`stat-card ${cls}`} onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default", transition: "transform .15s, box-shadow .15s", position: "relative" }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(4,18,14,.12)"; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}>
      <div className="sc-lbl">{label}</div>
      <div className="sc-val">{value}</div>
      <div className="sc-sub">{sub}</div>
      {sparkColor && <SparkBars color={sparkColor} hi={sparkHi} />}
      <span className={`sc-chg ${chgType}`}>{chg}</span>
      {onClick && <div style={{ position: "absolute", bottom: 10, right: 12, fontSize: 9, color: "rgba(255,255,255,.3)", fontFamily: "var(--fm)" }}>{t("dash.clickToView")}</div>}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px solid var(--border)" }}>
      <div style={{ fontSize: 12, color: "var(--muted)" }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, fontFamily: mono ? "var(--fm)" : "var(--fb)", color: "var(--ink)", textAlign: "right", maxWidth: 220, wordBreak: "break-all" }}>{value ?? "—"}</div>
    </div>
  );
}

// ─── USER DETAIL PANEL (reused in multiple modals) ───────────────────────────

function UserDetailPanel({ user, onNavigate, onClose }) {
  const { t } = useLang();
  const avatarInitials = (user.name ?? "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div>
      <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "16px 18px", background: "var(--ink)", borderRadius: 12, marginBottom: 16 }}>
        {user.avatarUrl
          ? <img src={user.avatarUrl} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          : <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--fd)", fontWeight: 900, fontSize: 18, color: "var(--teal)", flexShrink: 0 }}>{avatarInitials}</div>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 16, color: "white" }}>{user.name ?? "—"}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.45)", marginTop: 2, fontFamily: "var(--fm)" }}>{user.email}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            <Badge status={user.subscriptionStatus} />
            {user.subscription?.platform && <span className="badge bg-blue" style={{ fontSize: 9 }}>{PLT_LABEL[user.subscription.platform] ?? user.subscription.platform}</span>}
            {user.subscription?.plan && <span className="badge bg-teal" style={{ fontSize: 9 }}>{user.subscription.plan}</span>}
          </div>
        </div>
      </div>
      <InfoRow label="Email" value={user.email} mono />
      <InfoRow label={t("dash.subscriptionStatus")} value={user.subscriptionStatus ?? "NONE"} />
      <InfoRow label={t("tbl.plan")} value={user.subscription?.plan ?? "—"} />
      <InfoRow label={t("tbl.platform")} value={PLT_LABEL[user.subscription?.platform] ?? user.subscription?.platform ?? "—"} />
      <InfoRow label={t("dash.subscriptionExpires")} value={user.subscription?.periodEnd ? new Date(user.subscription.periodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"} />
      {user.childProfile && <InfoRow label={t("tbl.child")} value={`${user.childProfile.childName}, ${user.childProfile.ageMonths} months old`} />}
      <InfoRow label={t("tbl.joined")} value={new Date(user.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} />
      <InfoRow label={t("dash.lastLogin")} value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : t("ui.never")} />
      {onNavigate && (
        <button className="btn btn-teal" style={{ width: "100%", justifyContent: "center", marginTop: 14 }}
          onClick={() => { onNavigate("subscriptions"); onClose(); }}>
          {t("dash.viewInSubscriptions")}
        </button>
      )}
    </div>
  );
}

// ─── ACTIVITY DETAIL MODAL ────────────────────────────────────────────────────

function ActivityDetailModal({ log, onClose }) {
  const { t } = useLang();
  const actionLabels = {
    "user.registered":               { icon: "👤", label: "User Registration" },
    "user.subscribed":               { icon: "💳", label: "New Subscription" },
    "video.play":                    { icon: "🎥", label: "Video Played" },
    "askgad.responded":              { icon: "🎤", label: "Dr. Gad Response Sent" },
    "admin.subscription.override":   { icon: "🛠", label: "Subscription Override" },
    "admin.subscription.cancel":     { icon: "❌", label: "Subscription Cancelled" },
    "admin.subscription.restore":    { icon: "✅", label: "Subscription Restored" },
    "admin.email.sent":              { icon: "✉️", label: "Email Sent to User" },
    "payment.mobilemoney.success":   { icon: "📱", label: "Mobile Money Payment" },
    "payment.mobilemoney.pending":   { icon: "⏳", label: "Pending Mobile Money" },
    "payment.mobilemoney.failed":    { icon: "❌", label: "Failed Mobile Money" },
    "admin.payment.manual_activate": { icon: "⚡", label: "Manual Activation" },
  };
  const info = actionLabels[log.action] ?? { icon: "⚡", label: log.action };
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-ttl">{info.icon} {t("dash.activityDetail")} <CloseX onClick={onClose} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: 16, background: "var(--ink)", borderRadius: 12, marginBottom: 16 }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{info.icon}</div>
          <div>
            <div style={{ fontFamily: "var(--fd)", fontWeight: 900, fontSize: 15, color: "white" }}>{info.label}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.45)", marginTop: 3, fontFamily: "var(--fm)" }}>
              {new Date(log.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {[["Action", log.action], ["Details", log.details || "—"], [t("dash.userId"), log.userId || "System"], [t("dash.adminId"), log.adminId || "—"]].map(([label, value]) => (
            <div key={label} style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, fontFamily: label === "Action" || label === "User ID" || label === "Admin ID" ? "var(--fm)" : "var(--fb)", color: "var(--ink)", wordBreak: "break-all" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── WEEK SIGNUPS DETAIL MODAL ────────────────────────────────────────────────

function WeekDetailModal({ week, onClose, onNavigate }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => usersApi.list({ page: 1, limit: 50 }));
  const users = data?.users ?? [];
  const weekStart = new Date(week.start);
  const weekEnd   = new Date(week.end);
  const weekUsers = users.filter(u => { const d = new Date(u.createdAt); return d >= weekStart && d <= weekEnd; });
  const [selUser, setSelUser] = useState(null);
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-ttl">📅 {t("dash.weekSignups", { week: week.week })} <CloseX onClick={onClose} /></div>
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          {[["📅 " + t("dash.period"), `${weekStart.toLocaleDateString("en-GB", { day:"numeric", month:"short" })} — ${weekEnd.toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })}`], ["👥 " + t("dash.signups"), week.count]].map(([l, v]) => (
            <div key={l} style={{ padding: "10px 16px", background: "var(--teal-dim)", borderRadius: 10, border: "1px solid var(--teal-lo)" }}>
              <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px" }}>{l}</div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 18, fontWeight: 900, color: "var(--teal)" }}>{v}</div>
            </div>
          ))}
        </div>
        {selUser ? (
          <div>
            <button className="btn btn-out btn-sm" style={{ marginBottom: 12 }} onClick={() => setSelUser(null)}>{t("ui.backToList")}</button>
            <UserDetailPanel user={selUser} onNavigate={onNavigate} onClose={onClose} />
          </div>
        ) : loading ? <Spinner /> : weekUsers.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--ghost)", fontSize: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
            {week.count} signup{week.count !== 1 ? "s" : ""} recorded in Week {week.week}.
          </div>
        ) : (
          <div className="tbl-wrap" style={{ maxHeight: 380, overflowY: "auto" }}>
            <table>
              <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.status")}</th><th>{t("tbl.platform")}</th><th>{t("tbl.child")}</th><th>{t("tbl.joined")}</th></tr></thead>
              <tbody>{weekUsers.map(u => (
                <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setSelUser(u)}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td><div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{u.email}</div></td>
                  <td><Badge status={u.subscriptionStatus} /></td>
                  <td>{u.subscription?.platform ? <span className="badge bg-blue" style={{ fontSize: 9 }}>{PLT_LABEL[u.subscription.platform] ?? u.subscription.platform}</span> : <span style={{ color: "var(--ghost)", fontSize: 11 }}>—</span>}</td>
                  <td style={{ fontSize: 12, color: "var(--muted)" }}>{u.childProfile ? `${u.childProfile.childName}, ${u.childProfile.ageMonths}mo` : "—"}</td>
                  <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MODULE COMPLETION DETAIL MODAL ──────────────────────────────────────────

function ModuleDetailModal({ mod, onClose }) {
  const { t } = useLang();
  const { data, loading } = useApi(() => usersApi.list({ status: "active", limit: 100 }));
  const users = data?.users ?? [];
  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-ttl"><div style={{ display: "flex", alignItems: "center", gap: 8 }}><span className="tc">{mod.code}</span><span>{mod.title}</span></div><CloseX onClick={onClose} /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
          {[["✅ " + t("dash.completions"), mod.completions, "var(--teal)"], ["📈 " + t("dash.completionRate"), `${mod.completionRate}%`, "var(--amber)"], ["👥 " + t("dash.activeSubscribersModal"), data?.total ?? "—", "var(--blue)"]].map(([l, v, c]) => (
            <div key={l} style={{ textAlign: "center", padding: 12, background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: c }}>{v}</div>
              <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 16, padding: "12px 14px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12, color: "var(--muted)" }}>
            <span>{t("dash.completionRateLabel")}</span>
            <strong style={{ color: mod.completionRate >= 70 ? "var(--teal)" : mod.completionRate >= 40 ? "var(--amber)" : "var(--rose)" }}>{mod.completionRate}%</strong>
          </div>
          <ProgBar pct={mod.completionRate} />
        </div>
        {loading ? <Spinner /> : (
          <div className="tbl-wrap" style={{ maxHeight: 260, overflowY: "auto" }}>
            <table>
              <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.status")}</th><th>{t("tbl.plan")}</th><th>{t("dash.childAge")}</th></tr></thead>
              <tbody>{users.slice(0, 20).map(u => (
                <tr key={u.id}>
                  <td><div style={{ fontWeight: 700, fontSize: 12 }}>{u.name}</div><div style={{ fontSize: 10, color: "var(--ghost)" }}>{u.email}</div></td>
                  <td><Badge status={u.subscriptionStatus} /></td>
                  <td style={{ fontSize: 11 }}>{u.subscription?.plan ?? "—"}</td>
                  <td style={{ fontSize: 11, color: "var(--muted)" }}>{u.childProfile?.ageMonths ? `${u.childProfile.ageMonths}mo` : "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ACTIVE SUBSCRIBERS MODAL ─────────────────────────────────────────────────

function ActiveSubscribersModal({ onClose, onNavigate }) {
  const { t } = useLang();
  const [platFilter, setPlatFilter] = useState("");
  const [selUser, setSelUser] = useState(null);
  const { data, loading } = useApi(() => usersApi.list({ status: "active", limit: 200 }));
  const allUsers = data?.users ?? [];
  const users = platFilter ? allUsers.filter(u => u.subscription?.platform === platFilter) : allUsers;
  const total = platFilter ? users.length : (data?.total ?? 0);

  const PLATFORMS = [
    { key: "", label: t("dash.totalActive") },
    { key: "apple_iap",    label: "Apple IAP" },
    { key: "google_play",  label: "Google Play" },
    { key: "stripe",       label: "Stripe" },
    { key: "flutterwave",  label: "Flutterwave" },
  ];

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 660 }}>
        <div className="modal-ttl">👥 {t("dash.activeSubscribersModal")} <CloseX onClick={onClose} /></div>

        {selUser ? (
          <div>
            <button className="btn btn-out btn-sm" style={{ marginBottom: 12 }} onClick={() => setSelUser(null)}>{t("ui.backToList")}</button>
            <UserDetailPanel user={selUser} onNavigate={onNavigate} onClose={onClose} />
          </div>
        ) : (
          <>
            {/* Platform filter chips */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
              {PLATFORMS.map(p => (
                <button key={p.key}
                  onClick={() => setPlatFilter(p.key)}
                  style={{
                    padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${platFilter === p.key ? (PLT_COLOR[p.key] || "var(--teal)") : "var(--border)"}`,
                    background: platFilter === p.key ? (PLT_COLOR[p.key] || "var(--teal)") : "var(--surface)",
                    color: platFilter === p.key ? "white" : "var(--muted)",
                    fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all .15s",
                    fontFamily: "var(--fb)",
                  }}>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Count strip */}
            <div style={{ background: "var(--teal-dim)", border: "1px solid var(--teal-lo)", borderRadius: 10, padding: "10px 16px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>
                {platFilter ? `${PLT_LABEL[platFilter] ?? platFilter} subscribers` : t("dash.allActiveSubscribers")}
              </span>
              <span style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color: "var(--teal)" }}>{loading ? "…" : total}</span>
            </div>

            {loading ? <Spinner /> : (
              <div className="tbl-wrap" style={{ maxHeight: 360, overflowY: "auto" }}>
                <table>
                  <thead><tr><th>{t("tbl.user")}</th><th>{t("tbl.plan")}</th><th>{t("tbl.platform")}</th><th>{t("tbl.child")}</th><th>{t("tbl.expires")}</th></tr></thead>
                  <tbody>{users.map(u => (
                    <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setSelUser(u)}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <td>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                        <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{u.email}</div>
                      </td>
                      <td><span className="badge bg-teal">{u.subscription?.plan ?? "—"}</span></td>
                      <td>
                        <span className="badge bg-blue" style={{ fontSize: 9, background: PLT_COLOR[u.subscription?.platform] ?? undefined }}>
                          {PLT_LABEL[u.subscription?.platform] ?? u.subscription?.platform ?? "—"}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: "var(--muted)" }}>{u.childProfile ? `${u.childProfile.childName}, ${u.childProfile.ageMonths}mo` : "—"}</td>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>
                        {u.subscription?.periodEnd ? new Date(u.subscription.periodEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {total > 100 && <div style={{ padding: "8px 0 0", fontSize: 11, color: "var(--ghost)", textAlign: "center" }}>{t("ui.showingOf", { n: 100, total })}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("common.close")}</button>
              <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => { onNavigate("subscriptions"); onClose(); }}>
                {t("dash.goToSubscriptions")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── REVENUE MODAL ────────────────────────────────────────────────────────────

function RevenueModal({ dash, onClose, onNavigate }) {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const today = now.toISOString().slice(0, 10);

  const [from, setFrom] = useState(firstOfMonth);
  const [to,   setTo]   = useState(today);
  const [preset, setPreset] = useState("month");

  const applyPreset = (p) => {
    setPreset(p);
    const n = new Date();
    if (p === "month")  { setFrom(new Date(n.getFullYear(), n.getMonth(), 1).toISOString().slice(0,10)); setTo(n.toISOString().slice(0,10)); }
    if (p === "last30") { const d = new Date(n); d.setDate(d.getDate()-30); setFrom(d.toISOString().slice(0,10)); setTo(n.toISOString().slice(0,10)); }
    if (p === "last90") { const d = new Date(n); d.setDate(d.getDate()-90); setFrom(d.toISOString().slice(0,10)); setTo(n.toISOString().slice(0,10)); }
    if (p === "year")   { setFrom(n.getFullYear()+"-01-01"); setTo(n.toISOString().slice(0,10)); }
    if (p === "all")    { setFrom(""); setTo(""); }
  };

  const { data, loading } = useApi(() => adminApi.revenue(from || undefined, to || undefined), [from, to]);

  const totalPeriod  = data?.totalPeriod  ?? 0;
  const totalMonth   = data?.totalMonth   ?? 0;
  const totalAllTime = data?.totalAllTime ?? 0;
  const byPlatform   = data?.byPlatform   ?? [];
  const transactions = data?.transactions ?? [];

  const COLORS = ["var(--teal)", "var(--amber)", "#635bff", "var(--rose)", "#f5a623", "var(--ghost)"];
  const PLT_COLOR_MAP = { apple_iap: "var(--ink)", google_play: "#34a853", stripe: "#635bff", flutterwave: "#f5a623", manual: "var(--teal)", mobile_money: "var(--amber)" };
  const fmt = (n) => n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${Number(n).toFixed(0)}`;

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-ttl">💰 Revenue &amp; Earnings <CloseX onClick={onClose} /></div>

        {/* Preset tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {[["month","This Month"],["last30","Last 30d"],["last90","Last 90d"],["year","This Year"],["all","All Time"]].map(([p, label]) => (
            <button key={p} onClick={() => applyPreset(p)}
              style={{ padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, border: "1px solid var(--border)", cursor: "pointer",
                background: preset === p ? "var(--teal)" : "var(--surface)", color: preset === p ? "white" : "var(--muted)", transition: "all .15s" }}>
              {label}
            </button>
          ))}
        </div>

        {/* Custom date range */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
          <div style={{ fontSize: 11, color: "var(--ghost)", whiteSpace: "nowrap" }}>Custom range:</div>
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPreset("custom"); }}
            style={{ flex: 1, padding: "5px 8px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--ink)", fontSize: 12 }} />
          <div style={{ fontSize: 11, color: "var(--ghost)" }}>to</div>
          <input type="date" value={to} onChange={e => { setTo(e.target.value); setPreset("custom"); }}
            style={{ flex: 1, padding: "5px 8px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--ink)", fontSize: 12 }} />
        </div>

        {loading ? <Spinner /> : (<>
          {/* Hero cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { label: preset === "all" ? "All Time" : "Selected Period", value: fmt(totalPeriod), color: "var(--teal)", bg: "var(--teal-dim)" },
              { label: "This Month",  value: fmt(totalMonth),   color: "var(--amber)", bg: "var(--amber-lo)" },
              { label: "All Time",    value: fmt(totalAllTime), color: "#635bff",      bg: "rgba(99,91,255,.08)" },
            ].map(({ label, value, color, bg }) => (
              <div key={label} style={{ textAlign: "center", padding: "14px 10px", background: bg, borderRadius: 10, border: "1px solid " + color + "30" }}>
                <div style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color }}>{value}</div>
                <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 4 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* By platform */}
          {byPlatform.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>By Platform</div>
              <div style={{ display: "grid", gap: 6 }}>
                {byPlatform.map((p, i) => (
                  <div key={p.platform} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: PLT_COLOR_MAP[p.platform] ?? COLORS[i % COLORS.length] }} />
                      <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>{PLT_LABEL[p.platform] ?? p.platform}</span>
                      <span style={{ fontSize: 10, color: "var(--ghost)" }}>{p.count} payment{p.count !== 1 ? "s" : ""}</span>
                    </div>
                    <span style={{ fontFamily: "var(--fm)", fontSize: 13, fontWeight: 900, color: "var(--teal)" }}>{fmt(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transactions table */}
          {transactions.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>Transactions</div>
              <div className="tbl-wrap" style={{ maxHeight: 280, overflowY: "auto" }}>
                <table>
                  <thead><tr><th>User</th><th>Plan</th><th>Platform</th><th style={{ textAlign: "right" }}>Amount</th><th>Date</th></tr></thead>
                  <tbody>
                    {transactions.map(tx => (
                      <tr key={tx.id}>
                        <td>
                          <div style={{ fontWeight: 700, fontSize: 12 }}>{tx.userName ?? "—"}</div>
                          <div style={{ fontSize: 10, color: "var(--ghost)" }}>{tx.userEmail ?? ""}</div>
                        </td>
                        <td><span className="badge bg-teal" style={{ fontSize: 10 }}>{tx.plan ?? "—"}</span></td>
                        <td style={{ fontSize: 11, color: "var(--muted)" }}>{PLT_LABEL[tx.platform] ?? tx.platform}</td>
                        <td style={{ textAlign: "right", fontFamily: "var(--fm)", fontWeight: 900, color: "var(--teal)", fontSize: 13 }}>
                          {tx.currency === "USD" ? "$" : tx.currency + " "}{Number(tx.amount).toFixed(0)}
                        </td>
                        <td style={{ fontSize: 10, color: "var(--ghost)", whiteSpace: "nowrap" }}>
                          {tx.date ? new Date(tx.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "2-digit" }) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {transactions.length === 0 && byPlatform.length === 0 && (
            <Empty icon="💳" message="No payments in this period" />
          )}
        </>)}

        <button className="btn btn-teal" style={{ width: "100%", justifyContent: "center", marginTop: 16 }}
          onClick={() => { onNavigate("manual-payments"); onClose(); }}>
          View All Payments
        </button>
      </div>
    </div>
  );
}
// ─── DR. GAD QUEUE MODAL ─────────────────────────────────────────────────────

function GadQueueModal({ onClose, onNavigate, toast }) {
  const { t } = useLang();
  const { data, loading, reload } = useApi(() => askGadApi.queue({ limit: 100 }));
  const [tabFilter, setTabFilter] = useState("ALL");
  const [selSub, setSelSub] = useState(null);
  const [responding, setResponding] = useState(false);
  const [response, setResponse] = useState({ responseText: "", responseVideoR2Key: "" });
  const [saving, setSaving] = useState(false);

  const subs = data?.submissions ?? [];
  const counts = {
    ALL: subs.length,
    SUBMITTED: subs.filter(s => s.status === "SUBMITTED").length,
    UNDER_REVIEW: subs.filter(s => s.status === "UNDER_REVIEW").length,
    RESPONDED: subs.filter(s => s.status === "RESPONDED").length,
  };
  const filtered = tabFilter === "ALL" ? subs : subs.filter(s => s.status === tabFilter);

  const TABS = [
    { key: "ALL",          label: t("askgad.total"),       color: "var(--ink2)" },
    { key: "SUBMITTED",    label: t("askgad.pending"),     color: "var(--rose)" },
    { key: "UNDER_REVIEW", label: t("askgad.underReview"), color: "var(--amber)" },
    { key: "RESPONDED",    label: t("askgad.responded"),   color: "var(--teal)" },
  ];
  const STATUS_COLOR_GAD = { SUBMITTED: "var(--rose)", UNDER_REVIEW: "var(--amber)", RESPONDED: "var(--teal)" };

  const submitResponse = async () => {
    if (!response.responseText) return;
    setSaving(true);
    try {
      const { askGad: askGadApi2 } = await import("../lib/api.js");
      await askGadApi2.respond(selSub.id, response);
      reload(); setSelSub(null); setResponding(false); setResponse({ responseText: "", responseVideoR2Key: "" });
    } catch (e) { toast?.("Error: " + e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-ttl">🎤 Dr. Gad Queue <CloseX onClick={onClose} /></div>

        {selSub ? (
          <div>
            <button className="btn btn-out btn-sm" style={{ marginBottom: 12 }} onClick={() => { setSelSub(null); setResponding(false); }}>{t("dash.backToQueue")}</button>

            {/* Submission detail */}
            <div style={{ background: "var(--surface)", borderRadius: 12, padding: 16, border: "1px solid var(--border)", marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
                {selSub.user?.avatarUrl
                  ? <img src={selSub.user.avatarUrl} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
                  : <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: "var(--teal)" }}>{selSub.user?.name?.[0] ?? "?"}</div>}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>{selSub.user?.name ?? "—"}</div>
                  <div style={{ fontSize: 11, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{selSub.user?.email}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 12, background: `${STATUS_COLOR_GAD[selSub.status]}20`, color: STATUS_COLOR_GAD[selSub.status] }}>{selSub.status}</span>
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink2)", fontStyle: "italic", borderLeft: "3px solid var(--teal)", paddingLeft: 12 }}>
                "{selSub.questionText}"
              </div>
              {selSub.videoR2Key && (
                <div style={{ marginTop: 10, fontSize: 11, color: "var(--muted)" }}>
                  📹 <a href={selSub.videoR2Key} target="_blank" rel="noreferrer" style={{ color: "var(--teal)" }}>View submitted video</a>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>
                {t("dash.submitted")} {new Date(selSub.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>

            {selSub.status === "RESPONDED" ? (
              <div style={{ background: "var(--teal-dim)", borderRadius: 10, padding: 14, border: "1px solid var(--teal-lo)", marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--teal)", textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 8 }}>{t("dash.responseSent")}</div>
                <div style={{ fontSize: 13, color: "var(--ink2)", lineHeight: 1.5 }}>{selSub.responseText ?? "—"}</div>
                {selSub.responseVideoR2Key && <div style={{ marginTop: 8, fontSize: 11 }}>📹 <a href={selSub.responseVideoR2Key} target="_blank" rel="noreferrer" style={{ color: "var(--teal)" }}>{t("dash.responseVideo")}</a></div>}
              </div>
            ) : responding ? (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", marginBottom: 10 }}>{t("dash.writeResponse")}</div>
                <div className="f-grp">
                  <label className="f-lbl">{t("dash.responseTextLabel")}</label>
                  <textarea className="f-in" rows={4} style={{ resize: "vertical", fontFamily: "var(--fb)", fontSize: 13 }}
                    value={response.responseText} onChange={e => setResponse(r => ({ ...r, responseText: e.target.value }))}
                    placeholder="Type your response to the parent…" />
                </div>
                <div className="f-grp">
                  <label className="f-lbl">{t("dash.responseVideoUrl")}</label>
                  <input className="f-in" value={response.responseVideoR2Key} onChange={e => setResponse(r => ({ ...r, responseVideoR2Key: e.target.value }))} placeholder="YouTube or Cloudflare URL" />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button className="btn btn-out" onClick={() => setResponding(false)}>{t("common.cancel")}</button>
                  <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={submitResponse} disabled={saving || !response.responseText}>
                    {saving ? t("common.sending") : t("dash.sendResponse")}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 10 }}>
                <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => setResponding(true)}>
                  {t("dash.respondToQuestion")}
                </button>
                <button className="btn btn-out" onClick={() => { onNavigate("askgad"); onClose(); }}>
                  {t("dash.openInAskGad")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Stat chips */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 }}>
              {TABS.map(tab => (
                <div key={tab.key} style={{ textAlign: "center", padding: "10px 6px", background: "var(--surface)", borderRadius: 8, border: "1px solid var(--border)" }}>
                  <div style={{ fontFamily: "var(--fd)", fontSize: 22, fontWeight: 900, color: tab.color }}>{counts[tab.key]}</div>
                  <div style={{ fontSize: 9, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".4px", marginTop: 2 }}>{tab.label}</div>
                </div>
              ))}
            </div>

            {/* Filter tabs */}
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {TABS.map(tab => (
                <button key={tab.key} onClick={() => setTabFilter(tab.key)}
                  style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${tabFilter === tab.key ? tab.color : "var(--border)"}`, background: tabFilter === tab.key ? `${tab.color}18` : "var(--surface)", color: tabFilter === tab.key ? tab.color : "var(--muted)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "var(--fb)", transition: "all .15s" }}>
                  {tab.label} {counts[tab.key] > 0 && `(${counts[tab.key]})`}
                </button>
              ))}
            </div>

            {loading ? <Spinner /> : filtered.length === 0 ? <Empty icon="🎤" message={t("dash.noSubmissions")} /> : (
              <div style={{ maxHeight: 380, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {filtered.map(s => (
                  <div key={s.id} onClick={() => setSelSub(s)}
                    style={{ padding: "12px 14px", background: "var(--surface)", borderRadius: 10, border: `1px solid var(--border)`, borderLeft: `3px solid ${STATUS_COLOR_GAD[s.status]}`, cursor: "pointer", transition: "background .15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                    onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", lineHeight: 1.5, marginBottom: 8 }}>
                      "{s.questionText?.slice(0, 110)}{s.questionText?.length > 110 ? "…" : ""}"
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "var(--teal)" }}>{s.user?.name?.[0] ?? "?"}</div>
                      <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{s.user?.name}</span>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: `${STATUS_COLOR_GAD[s.status]}18`, color: STATUS_COLOR_GAD[s.status], fontWeight: 700, marginLeft: "auto" }}>{s.status}</span>
                      <span style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{new Date(s.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("common.close")}</button>
              <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => { onNavigate("askgad"); onClose(); }}>
                {t("dash.goToAskGad")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── DONATIONS MODAL ──────────────────────────────────────────────────────────

function DonationsModal({ onClose, onNavigate }) {
  const { t } = useLang();
  const { data: statsData }  = useApi(() => donationsApi.stats());
  const { data: listData, loading } = useApi(() => donationsApi.list({ limit: 100 }));
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selDon, setSelDon] = useState(null);

  const stats = statsData ?? {};
  const allDonations = listData?.donations ?? [];
  const filtered = statusFilter === "ALL" ? allDonations : allDonations.filter(d => d.status === statusFilter);

  const counts = {
    ALL:       allDonations.length,
    COMPLETED: allDonations.filter(d => d.status === "COMPLETED").length,
    PENDING:   allDonations.filter(d => d.status === "PENDING").length,
    FAILED:    allDonations.filter(d => d.status === "FAILED").length,
  };
  const STATUS_TABS = [
    { key: "ALL",       label: "All",       color: "var(--ink2)" },
    { key: "COMPLETED", label: "Completed", color: "var(--teal)" },
    { key: "PENDING",   label: "Pending",   color: "var(--amber)" },
    { key: "FAILED",    label: "Failed",    color: "var(--rose)" },
  ];

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-ttl">💝 {t("dash.donationsOverview")} <CloseX onClick={onClose} /></div>

        {selDon ? (
          <div>
            <button className="btn btn-out btn-sm" style={{ marginBottom: 12 }} onClick={() => setSelDon(null)}>{t("ui.backToList")}</button>
            <div style={{ background: "var(--ink)", borderRadius: 12, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 4 }}>{t("dash.donationAmount")}</div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 36, fontWeight: 900, color: "var(--teal)" }}>${Number(selDon.amountUsd ?? 0).toFixed(2)}</div>
              <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 10, background: `${STATUS_COLOR[selDon.status] ?? "var(--ghost)"}22`, color: STATUS_COLOR[selDon.status] ?? "var(--ghost)", fontWeight: 700 }}>{selDon.status}</span>
                <span className="badge bg-blue" style={{ fontSize: 9 }}>{selDon.paymentMethod}</span>
              </div>
            </div>
            <InfoRow label={t("tbl.donor")} value={selDon.donorName ?? t("ui.anonymous")} />
            <InfoRow label="Email" value={selDon.donorEmail ?? "—"} mono />
            <InfoRow label={t("tbl.campaign")} value={selDon.campaign ?? "General"} />
            <InfoRow label={t("tbl.method")} value={selDon.paymentMethod} />
            <InfoRow label="Transaction Ref" value={selDon.txRef ?? "—"} mono />
            <InfoRow label={t("tbl.date")} value={new Date(selDon.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
            <button className="btn btn-teal" style={{ width: "100%", justifyContent: "center", marginTop: 14 }}
              onClick={() => { onNavigate("donations"); onClose(); }}>
              {t("dash.viewInDonations")}
            </button>
          </div>
        ) : (
          <>
            {/* Stats hero */}
            <div style={{ textAlign: "center", padding: 18, background: "var(--ink)", borderRadius: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 6 }}>{t("dash.totalReceived")}</div>
              <div style={{ fontFamily: "var(--fd)", fontSize: 40, fontWeight: 900, color: "var(--teal)" }}>${(stats.totalUsd ?? 0).toLocaleString()}</div>
              <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 10 }}>
                {[["Completed", counts.COMPLETED, "var(--teal)"], ["Pending", counts.PENDING, "var(--amber)"], ["Failed", counts.FAILED, "var(--rose)"]].map(([l, v, c]) => (
                  <div key={l} style={{ textAlign: "center" }}>
                    <div style={{ fontFamily: "var(--fd)", fontSize: 16, fontWeight: 900, color: c }}>{v}</div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,.35)", textTransform: "uppercase", letterSpacing: ".4px" }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Filter tabs */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {STATUS_TABS.map(tab => (
                <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
                  style={{ padding: "5px 12px", borderRadius: 20, border: `1.5px solid ${statusFilter === tab.key ? tab.color : "var(--border)"}`, background: statusFilter === tab.key ? `${tab.color}18` : "var(--surface)", color: statusFilter === tab.key ? tab.color : "var(--muted)", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "var(--fb)", transition: "all .15s" }}>
                  {tab.label} {counts[tab.key] > 0 && `(${counts[tab.key]})`}
                </button>
              ))}
            </div>

            {loading ? <Spinner /> : filtered.length === 0 ? <Empty icon="💝" message="No donations in this category" /> : (
              <div className="tbl-wrap" style={{ maxHeight: 300, overflowY: "auto" }}>
                <table>
                  <thead><tr><th>{t("tbl.donor")}</th><th>{t("tbl.amount")}</th><th>{t("tbl.method")}</th><th>{t("tbl.campaign")}</th><th>{t("tbl.status")}</th><th>{t("tbl.date")}</th></tr></thead>
                  <tbody>{filtered.map((d, i) => (
                    <tr key={i} style={{ cursor: "pointer" }} onClick={() => setSelDon(d)}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{d.donorName ?? "Anonymous"}</td>
                      <td style={{ fontFamily: "var(--fm)", fontWeight: 800, color: "var(--teal)" }}>${d.amountUsd}</td>
                      <td><span className="badge bg-blue" style={{ fontSize: 9 }}>{d.paymentMethod}</span></td>
                      <td style={{ fontSize: 11, color: "var(--muted)" }}>{d.campaign ?? "—"}</td>
                      <td><span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 10, background: `${STATUS_COLOR[d.status] ?? "var(--ghost)"}22`, color: STATUS_COLOR[d.status] ?? "var(--ghost)", fontWeight: 700 }}>{d.status}</span></td>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 10, color: "var(--ghost)" }}>{new Date(d.createdAt).toLocaleDateString("en-GB", { day:"numeric", month:"short" })}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button className="btn btn-out" style={{ flex: 1, justifyContent: "center" }} onClick={onClose}>{t("common.close")}</button>
              <button className="btn btn-teal" style={{ flex: 1, justifyContent: "center" }} onClick={() => { onNavigate("donations"); onClose(); }}>
                {t("dash.goToDonations")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────

export default function PageDashboard({ onNavigate, toast }) {
  const { t } = useLang();
  const { data: dash, loading } = useApi(() => adminApi.dashboard());
  const { data: trend }         = useApi(() => adminApi.signupTrend());
  const { data: signinTrend }   = useApi(() => adminApi.signinTrend());
  const { data: activity }      = useApi(() => adminApi.activityLog(1));
  const { data: mStats }        = useApi(() => adminApi.moduleStats());
  const { data: liveData }      = useApi(() => adminApi.liveUsers());
  const { data: revenueData }   = useApi(() => adminApi.revenue());

  const [modal,   setModal]   = useState(null);
  const [actLog,  setActLog]  = useState(null);
  const [selWeek, setSelWeek] = useState(null);
  const [selMod,  setSelMod]  = useState(null);
  const [selLive, setSelLive] = useState(null);

  if (loading) return <Spinner />;

  const d         = dash ?? {};
  const rawWeeks  = trend?.trend ?? [];
  const signinWks = signinTrend?.trend ?? [];
  const weeks     = rawWeeks.map(w => ({ ...w, signins: signinWks.find(s => s.week === w.week)?.count ?? 0 }));
  const logs      = activity?.logs ?? [];
  const mods      = mStats?.stats ?? [];
  const liveUsers = liveData?.users ?? [];

  const actionIcon = (action) => {
    const map = { "user.registered":"👤","user.subscribed":"💳","video.play":"🎥","askgad.responded":"🎤","admin.subscription.override":"🛠","admin.email.sent":"✉️","payment.mobilemoney.success":"📱","payment.mobilemoney.pending":"⏳","app.open":"📱" };
    return map[action] ?? "⚡";
  };

  return (
    <div>
      {/* Stat Cards */}
      <div className="stats-grid">
        <StatCard cls="sc-teal"  data-testid="stat-active-subs"  label={t("dash.activeSubscribers")} value={(d.activeSubscribers??0).toLocaleString()} sub={`of ${(d.totalUsers??0).toLocaleString()} registered`}   chg={`↑ +${d.weeklySignups??0} this week`}   sparkColor="var(--teal-lo)"  sparkHi="var(--teal)"  onClick={() => setModal("subscribers")} />
        <StatCard cls="sc-amber" data-testid="stat-revenue"       label="Total Earned"    value={`$${((revenueData?.totalAllTime??0)).toFixed(0)}`}  sub={`${new Date().toLocaleString("en-US",{month:"short"})}: $${((revenueData?.totalMonth??0)).toFixed(0)}`}  chg="↑ Live from payments"  sparkColor="var(--amber-lo)" sparkHi="var(--amber)" onClick={() => setModal("revenue")} />
        <StatCard cls="sc-rose"  data-testid="stat-gad-queue"    label={t("dash.gadQueue")}          value={d.gadQueue??0}                                sub={`${d.gadPending??0} pending response`}               chg={`${d.gadPending??0} awaiting reply`}     chgType="chg-nt" sparkColor="var(--rose-lo)" sparkHi="var(--rose)" onClick={() => setModal("gadqueue")} />
        <StatCard cls="sc-blue"  data-testid="stat-donations"    label={t("dash.totalDonations")}    value={`$${((d.donations??0)/1000).toFixed(1)}k`}    sub={`${d.scholarships??0} scholarships granted`}         chg={`↑ +$${d.donationsWeek??0} this week`}  sparkColor="var(--blue-lo)"  sparkHi="var(--blue)"  onClick={() => setModal("donations")} />
      </div>

      {/* ── Platform breakdown + health strip ── */}
      {(d.activeSubscribers > 0 || d.weeklySignups > 0) && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">📊</div>{t("dash.revenueHealth")}</div>
            <span style={{ fontSize: 11, background: "var(--teal-dim)", color: "var(--teal)", padding: "3px 10px", borderRadius: 20, fontWeight: 700, cursor: "pointer" }}
              onClick={() => setModal("revenue")}>{t("dash.fullBreakdown")}</span>
          </div>
          <div className="card-body" style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            {[
              { label: t("dash.mrr"), value: `$${((d.mrr??0)/1000).toFixed(1)}k`, color: "var(--teal)", icon: "💰" },
              { label: t("dash.subscribers"), value: (d.activeSubscribers??0).toLocaleString(), color: "var(--blue)", icon: "👥" },
              { label: t("dash.churnRate"), value: `${d.churnRate??0}%`, color: d.churnRate > 5 ? "var(--rose)" : "var(--teal)", icon: "📉" },
              { label: t("dash.conversion"), value: `${d.conversionRate??0}%`, color: "var(--amber)", icon: "🎯" },
              { label: t("dash.trialUsers"), value: (d.trialUsers??0).toLocaleString(), color: "var(--amber)", icon: "🎁" },
              { label: t("dash.scholarships"), value: (d.scholarships??0).toLocaleString(), color: "var(--teal)", icon: "🎓" },
            ].map(m => (
              <div key={m.label} style={{ textAlign: "center", padding: "10px 16px", background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)", flex: "1 1 100px" }}>
                <div style={{ fontSize: 18, marginBottom: 2 }}>{m.icon}</div>
                <div style={{ fontFamily: "var(--fd)", fontSize: 20, fontWeight: 900, color: m.color }}>{m.value}</div>
                <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginTop: 2 }}>{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="cg2">
        {/* Weekly Signups */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">📅</div>{t("dash.weeklySignups")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("dash.clickBarForDetails")}</span>
          </div>
          <div className="card-body">
            {weeks.length === 0 ? <Empty icon="📊" message={t("dash.noSignupData")} /> : (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={weeks} margin={{ top: 4, right: 4, left: -28, bottom: 0 }} barCategoryGap="25%" barGap={2}>
                    <XAxis dataKey="week" tickFormatter={w => `W${w}`} tick={{ fontSize: 10, fill: "var(--ghost)", fontFamily: "var(--fm)" }} axisLine={false} tickLine={false} />
                    <YAxis hide allowDecimals={false} />
                    <Tooltip cursor={{ fill: "var(--teal-dim)", radius: 4 }}
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const w = payload[0].payload;
                        return (
                          <div style={{ background: "var(--ink)", color: "white", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontFamily: "var(--fd)" }}>
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>Week {w.week}</div>
                            <div style={{ display: "flex", gap: 12 }}>
                              <div><div style={{ color: "#0db87e", fontWeight: 900, fontSize: 15 }}>{w.count}</div><div style={{ fontSize: 9, color: "rgba(255,255,255,.4)", textTransform: "uppercase" }}>{t("dash.newSignups")}</div></div>
                              <div><div style={{ color: "#f5a623", fontWeight: 900, fontSize: 15 }}>{w.signins}</div><div style={{ fontSize: 9, color: "rgba(255,255,255,.4)", textTransform: "uppercase" }}>{t("dash.activeUsers")}</div></div>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: 6 }} formatter={v => v === "count" ? t("dash.newSignups") : t("dash.activeUsers")} />
                    <Bar dataKey="count"   name="count"   radius={[4,4,0,0]} fill="#0db87e" onClick={w => setSelWeek(w)} style={{ cursor: "pointer" }} />
                    <Bar dataKey="signins" name="signins" radius={[4,4,0,0]} fill="#f5a623" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="chart-row">
                  <div><div className="cr-lbl">{t("dash.peakSignups")}</div><div className="cr-val" style={{ color: "var(--teal)" }}>{Math.max(...weeks.map(w => w.count))}</div></div>
                  <div><div className="cr-lbl">{t("dash.peakActive")}</div><div className="cr-val" style={{ color: "var(--amber)" }}>{Math.max(...weeks.map(w => w.signins))}</div></div>
                  <div><div className="cr-lbl">{t("dash.totalSignups")}</div><div className="cr-val">{weeks.reduce((a, w) => a + w.count, 0)}</div></div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">⚡</div>{t("dash.liveActivity")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("ui.clickForDetails")}</span>
          </div>
          <div style={{ padding: "10px 16px" }}>
            {logs.length === 0 ? <Empty icon="📋" message={t("dash.noActivity")} /> : logs.slice(0, 8).map((a, i) => {
              const deviceIcon = a.device === "iOS" ? "📱" : a.device === "Android" ? "🤖" : a.device === "macOS" ? "🍎" : a.device === "Windows" ? "🪟" : a.device === "Linux" ? "🐧" : null;
              // Use 2-letter ISO code (e.g. "GN") not the full name ("Guinea")
              const countryFlag = a.countryCode
                ? a.countryCode.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)))
                : null;

              return (
                <div key={i} data-testid="activity-log-item" className="act-item" onClick={() => setActLog(a)}
                  style={{ cursor: "pointer", borderRadius: 8, padding: "9px 10px", marginBottom: 4, transition: "background .15s", display: "flex", alignItems: "center", gap: 10 }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>

                  {/* Avatar / action icon */}
                  <div style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, overflow: "hidden",
                    background: a.userAvatar ? "transparent" : "var(--teal-dim)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: a.userAvatar ? 0 : 14, border: "1.5px solid var(--border)" }}>
                    {a.userAvatar
                      ? <img src={a.userAvatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span>{a.userName ? a.userName[0].toUpperCase() : actionIcon(a.action)}</span>
                    }
                  </div>

                  {/* Main info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Row 1: name + action */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                      {a.userName && (
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", flexShrink: 0, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {a.userName}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: "var(--ink2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.details || a.action}
                      </span>
                    </div>
                    {/* Row 2: device + country + time */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {deviceIcon && a.device && (
                        <span style={{ fontSize: 10, color: "var(--ghost)", display: "flex", alignItems: "center", gap: 3 }}>
                          {deviceIcon} {a.device}
                        </span>
                      )}
                      {countryFlag && a.country && (
                        <span style={{ fontSize: 10, color: "var(--ghost)", display: "flex", alignItems: "center", gap: 3 }}>
                          {countryFlag} {a.country}
                        </span>
                      )}
                      {a.ipAddress && (
                        <span style={{ fontSize: 9, color: "var(--ghost)", fontFamily: "var(--fm)" }}>
                          {a.ipAddress}
                        </span>
                      )}
                      <span style={{ fontSize: 9, color: "var(--ghost)", fontFamily: "var(--fm)", marginLeft: "auto", whiteSpace: "nowrap" }}>
                        {new Date(a.createdAt).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                      </span>
                    </div>
                  </div>

                  <div style={{ fontSize: 9, color: "var(--ghost)", flexShrink: 0 }}>›</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Live Logged-In Users */}
      {liveUsers.length > 0 && (
        <div className="card">
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">🟢</div>{t("dash.recentlyActive")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("dash.last6")}</span>
          </div>
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>{t("tbl.user")}</th><th>{t("dash.subscriptionStatus")}</th><th>{t("dash.ipAddress")}</th><th>{t("dash.osPlatform")}</th><th>{t("dash.lastLogin")}</th></tr></thead>
              <tbody>{liveUsers.map(u => (
                <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setSelLive(u)}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {u.avatarUrl
                        ? <img src={u.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                        : <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--teal-dim)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "var(--teal)", flexShrink: 0 }}>{(u.name ?? "U")[0]}</div>}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                        <div style={{ fontSize: 10, color: "var(--ghost)", fontFamily: "var(--fm)" }}>{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td><Badge status={u.subscriptionStatus} /></td>
                  <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--muted)" }}>
                    {u.lastLoginIp
                      ? <span style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 7px", fontSize: 10 }}>🌐 {u.lastLoginIp}</span>
                      : <span style={{ color: "var(--ghost)" }}>—</span>}
                  </td>
                  <td>
                    {u.lastLoginOs
                      ? <span className="badge bg-blue" style={{ fontSize: 9 }}>
                          {u.lastLoginOs === "Windows" ? "🪟" : u.lastLoginOs === "macOS" ? "🍎" : u.lastLoginOs === "iOS" ? "📱" : u.lastLoginOs === "Android" ? "🤖" : "🐧"} {u.lastLoginOs}
                        </span>
                      : <span style={{ color: "var(--ghost)", fontSize: 11 }}>—</span>}
                  </td>
                  <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--teal)", display: "inline-block", flexShrink: 0 }} />
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("en-GB", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }) : "—"}
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {/* Module Completion Rates */}
      {mods.length > 0 && (
        <div className="card">
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">🏆</div>{t("dash.moduleCompletion")}</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{t("ui.clickForDetails")}</span>
          </div>
          <div style={{ padding: "12px 16px" }}>
            {mods.map(m => (
              <div key={m.code} style={{ marginBottom: 10, cursor: "pointer", padding: "8px 6px", borderRadius: 8, transition: "background .15s" }}
                onClick={() => setSelMod(m)}
                onMouseEnter={e => e.currentTarget.style.background = "var(--teal-dim)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
                    <span className="tc">{m.code}</span>
                    <span style={{ fontSize: 12, color: "var(--ink2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.title}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontFamily: "var(--fm)", fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>{m.completionRate}%</span>
                    <span style={{ fontSize: 12, color: "var(--ghost)" }}>›</span>
                  </div>
                </div>
                <ProgBar pct={m.completionRate} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {modal === "subscribers" && <ActiveSubscribersModal onClose={() => setModal(null)} onNavigate={onNavigate} />}
      {modal === "revenue"     && <RevenueModal           dash={d} onClose={() => setModal(null)} onNavigate={onNavigate} />}
      {modal === "gadqueue"    && <GadQueueModal          onClose={() => setModal(null)} onNavigate={onNavigate} toast={toast} />}
      {modal === "donations"   && <DonationsModal         onClose={() => setModal(null)} onNavigate={onNavigate} />}
      {actLog  && <ActivityDetailModal log={actLog}  onClose={() => setActLog(null)} />}
      {selWeek && <WeekDetailModal     week={selWeek} onClose={() => setSelWeek(null)} onNavigate={onNavigate} />}
      {selMod  && <ModuleDetailModal   mod={selMod}   onClose={() => setSelMod(null)} />}
      {selLive && (
        <div className="modal-ov open">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-ttl">👤 {t("dash.userDetails")} <CloseX onClick={() => setSelLive(null)} /></div>
            <UserDetailPanel user={selLive} onNavigate={onNavigate} onClose={() => setSelLive(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
